import { CdkPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';

/**
 * HxSegmentBodyComponent - Content container for segmented control tabs. Provides slide animations when switching
 * between segments.
 */
@Component({
  selector: 'hx-segment-body',
  templateUrl: './segment-body.html',
  styleUrls: ['./segment-body.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkPortalOutlet],
})
export class HxSegmentBodyComponent extends HxBaseControl {
  /** The portal containing the content to display */
  readonly content = input<TemplatePortal | null>(null);

  /** Position relative to selected segment: -1 (left), 0 (center), 1 (right) */
  readonly position = input<number>(0);

  /** Whether this segment's content has been loaded (visited) */
  readonly isLoaded = input<boolean>(false);

  /** Whether to dynamically adjust height */
  readonly dynamicHeight = input<boolean>(false);

  /** Tabindex for content panel */
  readonly contentTabIndex = input<number | null>(null);

  /** Emitted when animation completes */
  readonly animationDone = output<void>();

  /**
   * Whether to render content based on position and loaded state. Renders selected segment (position 0) always, and
   * adjacent segments (positions -1, 1) only if they've been previously loaded. This enables slide animations while
   * maintaining lazy loading.
   */
  readonly shouldRender = computed(() => {
    const pos = this.position();
    const loaded = this.isLoaded();

    // Always render selected segment
    if (pos === 0) {
      return true;
    }

    // Render adjacent segments only if previously loaded (for slide animations)
    return (pos === -1 || pos === 1) && loaded;
  });
}
